import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Alert,
  Tag,
  Space,
  Typography,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  CopyOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import whatsappSettingsService from '../services/whatsappSettingsService';

const { Text, Paragraph, Link } = Typography;

const WhatsAppSettings = () => {
  const [form] = Form.useForm();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await whatsappSettingsService.getWhatsappSettings();
      setSettings(res.data || null);
      form.setFieldsValue({
        companyPhone: res.data?.companyPhone || '',
        wasenderapiToken: '', // never echo the token; user re-enters to change it
        wasenderapiWebhookSigningSecret: '', // never echoed either
      });
    } catch (err) {
      message.error('Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const webhookUrl = settings?.webhookSecret
    ? `${window.location.origin.replace(':3100', ':5200')}/api/webhooks/wasenderapi/${settings.webhookSecret}`
    : null;

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        companyPhone: values.companyPhone || '',
      };
      // Only send the token if the admin actually typed one in.
      if (values.wasenderapiToken && values.wasenderapiToken.trim()) {
        payload.wasenderapiToken = values.wasenderapiToken.trim();
      }
      // Only send the signing secret when the admin actually typed a new
      // value. Use the dedicated "Clear" button below to remove it.
      if (
        values.wasenderapiWebhookSigningSecret &&
        values.wasenderapiWebhookSigningSecret.trim()
      ) {
        payload.wasenderapiWebhookSigningSecret =
          values.wasenderapiWebhookSigningSecret.trim();
      }
      const res = await whatsappSettingsService.updateWhatsappSettings(payload);
      setSettings(res.data || null);
      form.setFieldsValue({ wasenderapiToken: '', wasenderapiWebhookSigningSecret: '' });
      const sessionMsg = res.sessionSummary ? ` ${res.sessionSummary}` : '';
      message.success(`WhatsApp settings saved.${sessionMsg}`);
    } catch (err) {
      const data = err.response?.data;
      const baseMsg = data?.message || 'Failed to save';
      // Pull the most useful tidbit out of WaSenderAPI's body.
      const upstream = data?.upstream;
      let detail = '';
      if (upstream) {
        if (typeof upstream === 'string') detail = upstream.slice(0, 200);
        else if (upstream.message) detail = String(upstream.message).slice(0, 200);
        else if (upstream.error) detail = String(upstream.error).slice(0, 200);
        else detail = JSON.stringify(upstream).slice(0, 200);
      }
      message.error(detail ? `${baseMsg} — ${detail}` : baseMsg, 8);
      console.log('[WhatsApp save] full response:', data);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await whatsappSettingsService.testConnection();
      if (res.success) {
        message.success(res.message || 'Connection successful');
      } else {
        message.error(res.message || 'Connection failed');
        console.log('[WhatsApp test] upstream:', res.upstream);
      }
    } catch (err) {
      const data = err.response?.data;
      message.error(data?.message || 'Connection failed');
      console.log('[WhatsApp test] full response:', data);
    } finally {
      setTesting(false);
    }
  };

  const handleRegenerate = async () => {
    setSaving(true);
    try {
      const res = await whatsappSettingsService.updateWhatsappSettings({ regenerateWebhookSecret: true });
      setSettings(res.data || null);
      message.success('Webhook URL regenerated. Update it in your WaSenderAPI session.');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to regenerate');
    } finally {
      setSaving(false);
    }
  };

  // Explicit clear: send "" so the server unsets the signing secret.
  const handleClearSigningSecret = async () => {
    setSaving(true);
    try {
      const res = await whatsappSettingsService.updateWhatsappSettings({
        wasenderapiWebhookSigningSecret: '',
      });
      setSettings(res.data || null);
      form.setFieldsValue({ wasenderapiWebhookSigningSecret: '' });
      message.success('Webhook signing secret cleared. Webhooks will no longer be HMAC-verified.');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to clear signing secret');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = async () => {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      message.success('Webhook URL copied');
    } catch {
      message.error('Could not copy — copy manually');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Text strong className="text-lg">WhatsApp via WaSenderAPI</Text>
        <Paragraph type="secondary" className="!mb-0 mt-1">
          One token enables WhatsApp messaging for every user in this tenant. Get a paid account and
          token from{' '}
          <Link href="https://wasenderapi.com" target="_blank" rel="noreferrer">
            wasenderapi.com
          </Link>
          {' '}— scan your WhatsApp QR there, then copy the personal access token here.
        </Paragraph>
      </div>

      <Card title="API Token">
        <Form form={form} layout="vertical" onFinish={handleSave} disabled={loading}>
          <div className="mb-3">
            <Tag color={settings?.hasToken ? 'success' : 'default'}>
              {settings?.hasToken ? 'Token configured' : 'Not configured'}
            </Tag>
          </div>
          <Form.Item
            name="wasenderapiToken"
            label="WaSenderAPI Personal Access Token"
            tooltip="Paste the token from wasenderapi.com → Settings → Personal Access Tokens"
            rules={settings?.hasToken ? [] : [{ required: true, message: 'Paste your token' }]}
          >
            <Input.Password
              placeholder={settings?.hasToken ? 'Leave blank to keep current token' : 'Bearer token'}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            name="companyPhone"
            label="Your WhatsApp number (optional)"
            tooltip="Informational — the number connected on WaSenderAPI."
          >
            <Input placeholder="+91 90000 00000" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              Save
            </Button>
            <Button
              onClick={handleTest}
              loading={testing}
              icon={<ExperimentOutlined />}
              disabled={!settings?.hasToken}
            >
              Test Connection
            </Button>
          </Space>
        </Form>
      </Card>

      <Card title="Webhook for inbound messages">
        {!settings?.hasToken ? (
          <Alert
            type="info"
            showIcon
            message="Save a token first — your webhook URL will be generated automatically."
          />
        ) : (
          <>
            <Paragraph type="secondary">
              Paste this URL into the <strong>Webhook URL</strong> field of your WaSenderAPI session
              (Sessions → Webhook Settings). Inbound WhatsApp messages from your customers will land
              in the matching lead's chat history.
            </Paragraph>
            <Space.Compact style={{ width: '100%' }}>
              <Input value={webhookUrl || ''} readOnly />
              <Tooltip title="Copy">
                <Button icon={<CopyOutlined />} onClick={copyWebhook}>Copy</Button>
              </Tooltip>
              <Popconfirm
                title="Regenerate webhook URL?"
                description="The old URL will stop working immediately. Update it in your WaSenderAPI dashboard."
                okText="Regenerate"
                onConfirm={handleRegenerate}
              >
                <Button danger icon={<ReloadOutlined />}>Regenerate</Button>
              </Popconfirm>
            </Space.Compact>
            <Alert
              className="mt-3"
              type="warning"
              showIcon
              message="Keep this URL secret"
              description="Anyone with this URL can post fake inbound messages to this tenant. Treat it like a password."
            />

            {/* Signing-secret HMAC verification — recommended */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Webhook Signing Secret</Text>
                <Tag
                  color={settings?.hasWebhookSigningSecret ? 'success' : 'warning'}
                  style={{ marginInlineStart: 8 }}
                >
                  {settings?.hasWebhookSigningSecret ? 'HMAC verification ON' : 'Not configured'}
                </Tag>
              </div>
              <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                Paste the <strong>Webhook Signing Secret</strong> shown on your
                WaSenderAPI session next to the webhook URL. We'll verify each
                inbound webhook by recomputing an HMAC-SHA256 of the payload and
                comparing it to the <code>x-wasenderapi-signature</code> header
                — so a leaked URL can't be used to forge messages.
              </Paragraph>
              <Form.Item
                name="wasenderapiWebhookSigningSecret"
                style={{ marginBottom: 8 }}
              >
                <Input.Password
                  placeholder={
                    settings?.hasWebhookSigningSecret
                      ? '•••• signing secret on file — leave blank to keep'
                      : 'Paste the signing secret from WaSenderAPI'
                  }
                  autoComplete="off"
                />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  icon={<SaveOutlined />}
                >
                  Save Signing Secret
                </Button>
                {settings?.hasWebhookSigningSecret && (
                  <Popconfirm
                    title="Clear the webhook signing secret?"
                    description="HMAC verification will be turned off. Inbound webhooks will only be authenticated by URL secret — anyone with the URL could forge messages."
                    okText="Clear"
                    okButtonProps={{ danger: true }}
                    onConfirm={handleClearSigningSecret}
                  >
                    <Button danger>Clear</Button>
                  </Popconfirm>
                )}
              </Space>
              {!settings?.hasWebhookSigningSecret && (
                <Alert
                  className="mt-3"
                  type="info"
                  showIcon
                  message="Recommended: enable HMAC verification"
                  description="Without a signing secret, any HTTPS request hitting your webhook URL is accepted. Configure one to harden against URL leaks."
                />
              )}
            </div>
          </>
        )}
      </Card>

      {/* Live webhook diagnostics — answers "is anything actually arriving?" */}
      {settings?.hasToken && (
        <Card
          title="Webhook activity"
          extra={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchSettings}
              loading={loading}
            >
              Refresh
            </Button>
          }
        >
          <WebhookActivityPanel activity={settings?.webhookActivity} />
        </Card>
      )}
    </div>
  );
};

// Renders a quick "is the webhook actually being hit?" panel. We pull
// timestamps off the tenant doc that the webhook handler bumps on every
// request, so the user can self-diagnose without log access.
const WebhookActivityPanel = ({ activity }) => {
  const lastInbound = activity?.lastInboundAt
    ? new Date(activity.lastInboundAt)
    : null;
  const lastError = activity?.lastErrorAt
    ? new Date(activity.lastErrorAt)
    : null;
  const count = activity?.inboundCount || 0;
  const minsSinceInbound = lastInbound
    ? Math.round((Date.now() - lastInbound.getTime()) / 60000)
    : null;
  const minsSinceError = lastError
    ? Math.round((Date.now() - lastError.getTime()) / 60000)
    : null;

  // Decide the headline status:
  //   - never seen anything → "no inbound webhooks yet"
  //   - latest event is a successful ping → "ok"
  //   - latest event is a signature rejection → "blocked"
  let status = 'idle';
  if (lastError && (!lastInbound || lastError > lastInbound)) {
    status = 'blocked';
  } else if (lastInbound) {
    status = 'ok';
  }

  const fmt = (d) =>
    d
      ? d.toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : '—';

  if (status === 'idle') {
    return (
      <div>
        <Alert
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
          message="No inbound webhooks received yet"
          description={
            <div>
              We haven't seen a single POST to your webhook URL. Check that:
              <ol style={{ marginTop: 8, paddingInlineStart: 22, marginBottom: 0 }}>
                <li>The webhook URL above is pasted into your WaSenderAPI session's Webhook Settings.</li>
                <li>Your server's public hostname (e.g. crmpro.qodequay.com) is reachable from the internet — local <code>localhost:5200</code> URLs won't work.</li>
                <li>The session on WaSenderAPI is "connected" (not paused / disconnected).</li>
                <li>If you're testing in dev, send yourself a WhatsApp message from a different number — your own session won't deliver inbound to itself.</li>
              </ol>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <Space size={8} wrap style={{ marginBottom: 12 }}>
        {status === 'ok' && (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Receiving webhooks
          </Tag>
        )}
        {status === 'blocked' && (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            Webhooks blocked
          </Tag>
        )}
        <Tag>
          {count.toLocaleString()} inbound {count === 1 ? 'request' : 'requests'} total
        </Tag>
      </Space>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Last inbound</Text>
          <div>
            <Text strong>{fmt(lastInbound)}</Text>
            {minsSinceInbound !== null && (
              <Text type="secondary" style={{ marginInlineStart: 6, fontSize: 12 }}>
                ({minsSinceInbound < 1 ? 'just now' : `${minsSinceInbound} min ago`})
              </Text>
            )}
          </div>
          {activity?.lastInboundEvent && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Event: <code>{activity.lastInboundEvent}</code>
            </Text>
          )}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Last rejection</Text>
          <div>
            <Text strong>{fmt(lastError)}</Text>
            {minsSinceError !== null && (
              <Text type="secondary" style={{ marginInlineStart: 6, fontSize: 12 }}>
                ({minsSinceError < 1 ? 'just now' : `${minsSinceError} min ago`})
              </Text>
            )}
          </div>
        </div>
      </div>

      {status === 'blocked' && activity?.lastErrorReason && (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message="WaSenderAPI is reaching us, but the request is being rejected"
          description={activity.lastErrorReason}
        />
      )}
    </div>
  );
};

export default WhatsAppSettings;
